import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Cloud, Wind, Waves, Eye, Thermometer, Droplets,
  AlertTriangle, CheckCircle2, RefreshCw, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weather code descriptions
const WEATHER_CODES = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Foggy',
  48: 'Depositing rime fog',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Dense drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  71: 'Slight snow',
  73: 'Moderate snow',
  75: 'Heavy snow',
  80: 'Slight rain showers',
  81: 'Moderate rain showers',
  82: 'Violent rain showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Thunderstorm with heavy hail'
};

export default function WeatherWidget({ latitude, longitude, compact = false }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (latitude && longitude) {
      fetchWeather();
    }
  }, [latitude, longitude]);

  const fetchWeather = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/weather/marine`, {
        params: { latitude, longitude },
        withCredentials: true
      });
      setWeather(data);
    } catch (err) {
      setError('Failed to load weather data');
      console.error('Weather fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!latitude || !longitude) {
    return null;
  }

  if (loading) {
    return (
      <Card className="border-2 border-[#E2E8F0]">
        <CardContent className="p-4 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 text-[#4A5568] animate-spin" />
          <span className="ml-2 text-[#4A5568]">Loading weather...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-[#E2E8F0]">
        <CardContent className="p-4 text-center">
          <p className="text-[#4A5568] text-sm">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchWeather} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!weather) return null;

  const getStatusColor = () => {
    switch (weather.status) {
      case 'good': return 'border-[#3CB371] bg-[#E8F6ED]';
      case 'caution': return 'border-[#E5A000] bg-[#FDF3E1]';
      case 'warning': return 'border-[#D32F2F] bg-[#FAEAEA]';
      default: return 'border-[#E2E8F0] bg-white';
    }
  };

  const getStatusTextColor = () => {
    switch (weather.status) {
      case 'good': return 'text-[#2A8A53]';
      case 'caution': return 'text-[#E5A000]';
      case 'warning': return 'text-[#D32F2F]';
      default: return 'text-[#4A5568]';
    }
  };

  const current = weather.current;
  const weatherDescription = WEATHER_CODES[current.weather_code] || 'Unknown';

  if (compact) {
    return (
      <Card className={`border-2 ${getStatusColor()}`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {weather.status === 'good' ? (
                <CheckCircle2 className="w-5 h-5 text-[#3CB371]" />
              ) : (
                <AlertTriangle className={`w-5 h-5 ${getStatusTextColor()}`} />
              )}
              <div>
                <p className={`font-bold text-sm ${getStatusTextColor()}`}>
                  {weather.status_message}
                </p>
                <p className="text-xs text-[#4A5568]">
                  {weatherDescription} • {current.temperature_c?.toFixed(0)}°C
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-black ${getStatusTextColor()}`}>
                {weather.safety_score}
              </p>
              <p className="text-xs text-[#4A5568]">Safety</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 ${getStatusColor()}`}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-[#4A5568]" />
            <span className="font-bold text-[#2B3A67]">Marine Weather</span>
          </div>
          <Button variant="ghost" size="icon" onClick={fetchWeather} className="h-8 w-8">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Safety Score */}
        <div className="text-center py-4 bg-white/50 rounded-xl">
          <p className={`text-4xl font-black ${getStatusTextColor()}`}>
            {weather.safety_score}%
          </p>
          <p className={`font-bold ${getStatusTextColor()}`}>
            {weather.status_message}
          </p>
          <p className="text-sm text-[#4A5568] mt-1">{weatherDescription}</p>
        </div>

        {/* Warnings */}
        {weather.warnings.length > 0 && (
          <div className="space-y-2">
            {weather.warnings.map((warning, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-2 p-2 rounded-lg ${
                  warning.severity === 'high' ? 'bg-[#FAEAEA]' : 'bg-[#FDF3E1]'
                }`}
              >
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${
                  warning.severity === 'high' ? 'text-[#D32F2F]' : 'text-[#E5A000]'
                }`} />
                <span className={`text-sm font-medium ${
                  warning.severity === 'high' ? 'text-[#D32F2F]' : 'text-[#E5A000]'
                }`}>
                  {warning.message}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Conditions Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 bg-white/50 rounded-xl">
            <Waves className="w-5 h-5 text-[#2B3A67]" />
            <div>
              <p className="text-xs text-[#4A5568]">Waves</p>
              <p className="font-bold text-[#2B3A67]">{current.wave_height_m?.toFixed(1) || 0}m</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-white/50 rounded-xl">
            <Wind className="w-5 h-5 text-[#2B3A67]" />
            <div>
              <p className="text-xs text-[#4A5568]">Wind</p>
              <p className="font-bold text-[#2B3A67]">{current.wind_speed_kmh?.toFixed(0) || 0} km/h</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-white/50 rounded-xl">
            <Thermometer className="w-5 h-5 text-[#2B3A67]" />
            <div>
              <p className="text-xs text-[#4A5568]">Temp</p>
              <p className="font-bold text-[#2B3A67]">{current.temperature_c?.toFixed(0) || 0}°C</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-white/50 rounded-xl">
            <Eye className="w-5 h-5 text-[#2B3A67]" />
            <div>
              <p className="text-xs text-[#4A5568]">Visibility</p>
              <p className="font-bold text-[#2B3A67]">{((current.visibility_m || 10000) / 1000).toFixed(0)} km</p>
            </div>
          </div>
        </div>

        {/* Wind Gusts */}
        {current.wind_gusts_kmh > current.wind_speed_kmh && (
          <div className="flex items-center gap-2 p-2 bg-white/50 rounded-lg text-sm">
            <Wind className="w-4 h-4 text-[#4A5568]" />
            <span className="text-[#4A5568]">
              Gusts up to <span className="font-bold">{current.wind_gusts_kmh?.toFixed(0)} km/h</span>
            </span>
          </div>
        )}

        {/* Rain Probability */}
        {current.precipitation_probability > 0 && (
          <div className="flex items-center gap-2 p-2 bg-white/50 rounded-lg text-sm">
            <Droplets className="w-4 h-4 text-[#4A5568]" />
            <span className="text-[#4A5568]">
              <span className="font-bold">{current.precipitation_probability}%</span> chance of rain
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Hook to get user's location
export function useUserLocation() {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLoading(false);
      },
      (err) => {
        // Use default Florida location if GPS fails
        setLocation({
          latitude: 25.7617,
          longitude: -80.1918 // Miami
        });
        setLoading(false);
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  return { location, loading, error };
}
